import { useFieldArray, useFormContext, Controller } from 'react-hook-form';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import Button from 'components/Button';
import FullHeightView from 'components/new/FullHeightView';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Column, Row } from 'further-ui/components/Layout';
import { InferredFeeSettingsFormType } from './FormWrapper';
import { createEmptyFeeTemplate } from './helpers';
import FeeTemplateSection from './FeeTemplateSection';
import { useState } from 'react';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useDisclosure } from 'further-ui/hooks';
import PageSection from 'components/new/PageSection';
import { TextField, Typography } from '@mui/material';
import Spacing from 'components/Spacing';
import FormField from 'further-ui/components/FormField';

const FeeSettingsForm: React.FC = () => {
  const mutationOpts = useHandleTrpcMutation();
  const { selectedVehicleId } = useLayoutContext();
  const [canEdit] = useHasPermissions([RolePermission.LpEditVehicle]);
  const updateWarningModal = useDisclosure();

  const [hasEditedTemplateWithCommitment, setHasEditedTemplateWithCommitment] =
    useState(false);
  const {
    expandedRows: collapsedTemplates,
    toggleRowExpansion: toggleTemplate,
  } = useExpandedRows();

  const updateVehicleFeeSettings = api.vehicle.updateFeeSettings.useMutation(
    mutationOpts({
      successMessage: 'Vehicle updated successfully.',
      onSuccess: () => {
        setHasEditedTemplateWithCommitment(false);
      },
    }),
  );

  const {
    control,
    getValues,
    reset,
    handleSubmit,
    formState: { dirtyFields },
  } = useFormContext<InferredFeeSettingsFormType>();

  const { fields } = useFieldArray({
    control,
    name: 'lpFeeTemplates',
  });

  const addNewFeeTemplate = () => {
    const { lpFeeTemplates, ...rest } = getValues();
    reset({
      ...rest,
      lpFeeTemplates: [
        ...lpFeeTemplates,
        {
          ...createEmptyFeeTemplate(lpFeeTemplates.length === 0),
        },
      ],
    });
  };

  const removeTemplate = (index: number) => {
    const { lpFeeTemplates, ...rest } = getValues();
    const afterRemoval = lpFeeTemplates.filter((_, i) => i !== index);
    const hasDefault = afterRemoval.some((t) => t.isDefaultFeeTemplate);

    const newLpFeeTemplates =
      afterRemoval.length > 0 && !hasDefault
        ? [
            { ...afterRemoval[0], isDefaultFeeTemplate: true },
            ...afterRemoval.slice(1),
          ]
        : afterRemoval;

    reset({
      ...rest,
      lpFeeTemplates: newLpFeeTemplates,
    });
  };

  const hasSignificantChanges = (index: number) => {
    const dirtyTemplateFields = dirtyFields.lpFeeTemplates?.[index];
    if (!dirtyTemplateFields) return false;

    // Check if any fields other than name or isDefaultFeeTemplate are dirty
    const { name, isDefaultFeeTemplate, lpFeeTemplateFees, ...otherFields } =
      dirtyTemplateFields;

    const feeTemplateHasChanges = Object.values(otherFields).some(
      (value) => value,
    );
    const feeTemplateFeesHaveChanges = lpFeeTemplateFees?.some((fee) =>
      Object.values(fee).some((value) => value),
    );
    return feeTemplateHasChanges || feeTemplateFeesHaveChanges;
  };

  const onSubmit = async (data: InferredFeeSettingsFormType) => {
    let hasChanges = false;

    for (let i = 0; i < data.lpFeeTemplates.length; i++) {
      const template = data.lpFeeTemplates[i];
      if (!template.hasCommitments && !template.hasApprovedFees) {
        continue;
      }
      if (template._id && hasSignificantChanges(i)) {
        hasChanges = true;
        break;
      }
    }

    if (hasChanges || hasEditedTemplateWithCommitment) {
      updateWarningModal.onOpen();
    } else {
      updateVehicleFeeSettings.mutate({
        ...data,
        vehicleId: selectedVehicleId ?? '',
      });
    }
  };

  const handleConfirmSubmit = () => {
    updateVehicleFeeSettings.mutate({
      ...getValues(),
      vehicleId: selectedVehicleId ?? '',
    });
    updateWarningModal.onClose();
  };

  return (
    <FullHeightView
      actionBar={
        canEdit ? (
          <Row fullWidth justify="flex-end">
            <Button
              variant="contained"
              color="primary"
              size="large"
              type="button"
              onClick={handleSubmit(onSubmit)}
              loading={updateVehicleFeeSettings.isPending}
            >
              Save
            </Button>
          </Row>
        ) : undefined
      }
    >
      <form>
        <PageSection
          spacing="lg"
          withoutBottomDivider
          title={
            <Row
              centered
              fullWidth
              justify="space-between"
              spacing="sm"
              breakpoint="md"
            >
              <Column spacing="xs">
                <Typography variant="inherit">Fee settings</Typography>
                <Typography variant="body2">
                  Create and manage fee templates that define how fees are
                  calculated.
                </Typography>
              </Column>
              {canEdit && (
                <Row>
                  <Button
                    variant="contained"
                    type="button"
                    onClick={addNewFeeTemplate}
                    size="large"
                  >
                    Add new template
                  </Button>
                </Row>
              )}
            </Row>
          }
        >
          <Spacing>
            <FormField
              label="Payee"
              layout="horizontal"
              labelSize="md"
              size="md"
              breakpoint="md"
            >
              <Controller
                name="payee.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    placeholder="e.g. Management Co Ltd"
                    error={!!error}
                    helperText={error?.message}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
              />
            </FormField>
          </Spacing>
          {fields.map((field, index) => (
            <FeeTemplateSection
              key={field.id}
              index={index}
              remove={removeTemplate}
              hasApprovedFees={!!field.hasApprovedFees}
              hasCommitments={!!field.hasCommitments}
              setHasEditedTemplateWithCommitment={
                setHasEditedTemplateWithCommitment
              }
              expanded={!collapsedTemplates.has(field.id)}
              onExpandedChange={() => toggleTemplate(field.id)}
            />
          ))}
        </PageSection>

        <ConfirmDialog
          open={updateWarningModal.isOpen}
          onClose={updateWarningModal.onClose}
          title="Warning"
          content="You are about to make changes to fee templates that have existing commitments. This will cause any unapproved fees to be recalculated. Are you sure you want to proceed?"
          actions={{
            confirm: {
              label: 'Proceed',
              onClick: handleConfirmSubmit,
            },
          }}
        />
      </form>
    </FullHeightView>
  );
};

export default FeeSettingsForm;
